import React, { useEffect, useState } from "react";
import { api, errMsg } from "../../lib/api";
import { Card, PageHeader, Input, Stat, Btn } from "../../components/UI";
import { Users, HeartHandshake, Repeat, DollarSign, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const usd = (n) => "$" + new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n || 0);
const currentYear = new Date().getFullYear();
const CYCLE_OPTIONS = [
  { key: "", label: "All time" },
  { key: String(currentYear), label: `${currentYear} cycle` },
  { key: String(currentYear + 1), label: `${currentYear + 1} cycle` },
  { key: String(currentYear - 2), label: `${currentYear - 2} cycle` },
];

export default function Donors() {
  const [donors, setDonors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [showExport, setShowExport] = useState(false);

  useEffect(() => {
    api.get("/donations/donors").then(r => setDonors(r.data));
    api.get("/donations/summary").then(r => setSummary(r.data));
  }, []);

  const filtered = donors.filter(d => !q || (d.name + d.email + (d.employer || "")).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="donors-page">
      <PageHeader
        eyebrow="Fundraising · CRM"
        title="Donors"
        subtitle="Donor profiles built automatically from live contributions — totals, recurring status, and history."
        action={
          <Btn onClick={() => setShowExport(true)} data-testid="donor-export-open-btn">
            <Download className="w-4 h-4" /> Export CSV
          </Btn>
        }
      />

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Total raised" value={usd(summary.total_raised)} icon={DollarSign} testid="donor-stat-raised" />
          <Stat label="Donors" value={summary.donor_count} icon={Users} testid="donor-stat-count" />
          <Stat label="Contributions" value={summary.contribution_count} icon={HeartHandshake} testid="donor-stat-contribs" />
          <Stat label="Recurring" value={summary.recurring_count} icon={Repeat} testid="donor-stat-recurring" />
        </div>
      )}

      <Card padded={false}>
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between gap-3">
          <h3 className="font-display text-lg font-bold">Donor CRM</h3>
          <Input placeholder="Search donors…" value={q} onChange={e => setQ(e.target.value)} className="max-w-xs" data-testid="donor-search" />
        </div>
        <table className="w-full text-sm" data-testid="donors-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="px-5 py-3">Donor</th>
              <th className="px-5 py-3">Employer / Occupation</th>
              <th className="px-5 py-3">Total given</th>
              <th className="px-5 py-3">Gifts</th>
              <th className="px-5 py-3">Recurring</th>
              <th className="px-5 py-3">Last gift</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(d => (
              <tr key={d.email} className="border-b border-white/5">
                <td className="px-5 py-2.5">
                  <div className="text-white">{d.name}</div>
                  <div className="text-xs text-slate-500">{d.email}{d.state && ` · ${d.state}`}</div>
                </td>
                <td className="px-5 py-2.5 text-xs text-slate-400">{d.employer}<div className="text-slate-600">{d.occupation}</div></td>
                <td className="px-5 py-2.5 font-mono text-white">{usd(d.total_donated)}</td>
                <td className="px-5 py-2.5 font-mono text-xs text-slate-400">{d.donation_count}</td>
                <td className="px-5 py-2.5">{d.is_recurring ? <span className="pill pill-active text-[10px]">monthly</span> : <span className="text-xs text-slate-600">—</span>}</td>
                <td className="px-5 py-2.5 text-xs text-slate-500">{(d.last_donation_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="6" className="px-5 py-8 text-center text-sm text-slate-500">No donors yet — contributions will appear here automatically.</td></tr>}
          </tbody>
        </table>
      </Card>

      {showExport && <DonorExportModal onClose={() => setShowExport(false)} />}
    </div>
  );
}

function DonorExportModal({ onClose }) {
  const [cycle, setCycle] = useState(String(currentYear));
  const [source, setSource] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (cycle) params.set("cycle", cycle);
      if (source) params.set("source", source);
      if (minAmount) params.set("min_amount", minAmount);
      if (maxAmount) params.set("max_amount", maxAmount);
      const { data, headers } = await api.get(`/donations/donors/export?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([data], { type: "text/csv" });
      const cd = headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `donors-${cycle || "all"}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Export failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/70 flex items-center justify-center p-4" data-testid="donor-export-modal">
      <Card className="max-w-md w-full">
        <div className="text-[10px] uppercase tracking-wider text-cyan-300 mb-1">Export donor CSV</div>
        <h3 className="font-display text-lg font-bold text-white mb-4">Filter your export</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Election cycle</label>
            <select value={cycle} onChange={(e) => setCycle(e.target.value)}
                    data-testid="donor-export-cycle"
                    className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none">
              {CYCLE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Min amount ($)</label>
              <input type="number" min="0" step="1" value={minAmount} onChange={(e) => setMinAmount(e.target.value)}
                     data-testid="donor-export-min"
                     placeholder="0"
                     className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Max amount ($)</label>
              <input type="number" min="0" step="1" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)}
                     data-testid="donor-export-max"
                     placeholder="e.g. 3300 (individual limit)"
                     className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Source (utm_source)</label>
            <input type="text" value={source} onChange={(e) => setSource(e.target.value)}
                   data-testid="donor-export-source"
                   placeholder="Leave blank for all sources (e.g. email, ctv, microsite)"
                   className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none placeholder-slate-500" />
          </div>
        </div>
        <div className="mt-6 flex gap-2 justify-end">
          <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn onClick={download} disabled={busy} data-testid="donor-export-download-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download CSV
          </Btn>
        </div>
      </Card>
    </div>
  );
}

